import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Appointment } from '@/lib/appointments-service';

interface CancelAppointmentDialogProps {
  appointment: Appointment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAppointmentCancelled: () => void;
}

const CancelAppointmentDialog = ({
  appointment,
  open,
  onOpenChange,
  onAppointmentCancelled,
}: CancelAppointmentDialogProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const handleCancel = async () => {
    if (!appointment) return;

    setLoading(true);
    try {
      // Update the booking status to cancelled
      const { error } = await supabase
        .from('bookings')
        .update({
          payment_status: 'cancelled',
          payment_notes: cancelReason 
            ? `${appointment.payment_notes || ''}\n\n[CANCELLED] ${new Date().toISOString()}: ${cancelReason}`.trim()
            : appointment.payment_notes,
          updated_at: new Date().toISOString(),
        })
        .eq('id', appointment.id);

      if (error) throw error;

      // Optionally try to delete Google Calendar event (don't fail if it doesn't work)
      if (appointment.calendar_event_id) {
        try {
          // This would need an edge function to delete calendar events
          // For now, just log that we'd delete it
          console.log('Would delete calendar event:', appointment.calendar_event_id);
        } catch (calendarError) {
          console.error('Failed to delete calendar event:', calendarError);
        }
      }

      toast({
        title: 'Appointment Cancelled',
        description: `Appointment for ${appointment.full_name} has been cancelled`,
      });

      setCancelReason('');
      onOpenChange(false);
      onAppointmentCancelled();
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel appointment. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!appointment) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Cancel Appointment
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to cancel this appointment? This action will mark the appointment as cancelled.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4 space-y-4">
          {/* Appointment Summary */}
          <div className="p-3 bg-muted rounded-lg">
            <p className="font-medium">{appointment.full_name}</p>
            <p className="text-sm text-muted-foreground">{appointment.email}</p>
            <p className="text-sm text-muted-foreground">{appointment.service}</p>
            {appointment.appointment_start && (
              <p className="text-sm text-muted-foreground mt-1">
                Scheduled: {new Date(appointment.appointment_start).toLocaleString()}
              </p>
            )}
          </div>

          {/* Cancel Reason */}
          <div className="space-y-2">
            <Label htmlFor="cancelReason">Reason for cancellation (optional)</Label>
            <Textarea
              id="cancelReason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter the reason for cancellation..."
              rows={3}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Keep Appointment
          </Button>
          <Button
            variant="destructive"
            onClick={handleCancel}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Cancelling...
              </>
            ) : (
              'Yes, Cancel Appointment'
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CancelAppointmentDialog;
